<!--
  Component: InputSelectYear

  This component creates an dropdown select input with a list of report years
  to choose from.

  Notes:
  - yearsLookup contains a mapping of reportCode to allowed years
  - Years are displayed in dropdown in range format (YYYY-XX) where XX is for
      year, and YYYY is year-1

  Props:
  - reportYear - currently selected report year (to default as selected option)
  - reportCode - code to look up allowed years to be displayed.  Should match
      report-level code in const.js files, e.g. 'sqs' = Snapshot
  - yearLabel - (optional) for customizing label for dropdown
-->

<template>
  <div class="input-select-year">
    <label for="select-year">{{dispYearLabel}}:</label>
    <select id="select-year" v-model="selectedYear" @change="yearChanged">
      <option v-for="year in reportYears"
        :key="year"
        :value="year">{{ formatYear(year) }}</option>
    </select>
  </div>
</template>

<script>
import Component, { mixins } from 'vue-class-component'
import { mapMutations } from 'vuex'

import { deepFreeze, yearRangeForReportYear } from '@/utils/data'

import { RouteMixin } from '@/mixin'

// list array should be sorted ascending by year.
// 'year' is the first report year for which the 'from' DBN is missing from the school list
// (i.e. if you open the report and select that year, 'from' DBN is not in the school selector
// dropdown list), 'to' DBN is there instead.
const DBN_CHANGE = deepFreeze([
  {year: 2023, from: '04M610', to: '03M610'},
  {year: 2024, from: '03M505', to: '04M505'},
  {year: 2025, from: '84M065', to: '84X649'},
  {year: 2025, from: '84K395', to: '84M424'},
  {year: 2025, from: '84X200', to: '84Q428'},
  {year: 2025, from: '84M330', to: '84X648'}
])

@Component({
  props: {
    reportYear: Number,
    reportCode: {type: String, default: 'sqs'},
    yearLabel: String
  },
  methods: {
    ...mapMutations({
      setRouteSchool (commit, payload) {
        commit(`${this.reportCode}/setRouteSchool`, payload)
      }
    }),
    formatYear: yearRangeForReportYear
  }
})
export default class InputSelectYear extends mixins(RouteMixin) {
  selectedYear = this.reportYear
  yearsLookup = {
    'sqs': [
      2025,
      2024,
      2023,
      2022,
      2021,
      2020,
      2019,
      2018,
      2017
    ]
  }

  /* Getters */

  get reportYears () {
    return this.yearsLookup[this.reportCode]
  }
  get dispYearLabel () {
    return (this.yearLabel === undefined) ? 'Year' : this.yearLabel
  }

  /* Methods */
  // this function can be more concise. This is for the benefit of being clear
  dbnChange (dbn, currentYear, gotoYear) {
    if (gotoYear > currentYear) {
      for (let i = 0; i < DBN_CHANGE.length; i++) {
        // anything happens after gotoYear is irrelevant
        if (DBN_CHANGE[i].year > gotoYear) break

        // changes happen before currentYear or in currentYear is irrelevant (dbn is a
        // valid dbn in currentYear already)
        if (DBN_CHANGE[i].year <= currentYear) continue

        if (DBN_CHANGE[i].from === dbn) {
          // This is new dbn in DBN_CHANGE[i].year, continue looping to chain additional updates
          dbn = DBN_CHANGE[i].to
        }
      }
    } else if (gotoYear < currentYear) {
      for (let i = DBN_CHANGE.length - 1; i >= 0; i--) {
        // a later dbn corresponds to a different dbn only BEFORE DBN_CHANGE[i].year, so anything that
        // happens in or before gotoYear is irrelevant
        if (DBN_CHANGE[i].year <= gotoYear) break

        // changes happen after currentYear is irrelevant
        if (DBN_CHANGE[i].year > currentYear) continue

        if (DBN_CHANGE[i].to === dbn) {
          // This is new dbn in DBN_CHANGE[i].year, continue looping to chain additional updates
          dbn = DBN_CHANGE[i].from
        }
      }
    }

    return dbn
  }

  yearChanged () {
    // This assumes that the names of the routes defined in router/index.js
    //  differ only by the year for the same report.  Retains all other
    //  parameters (URL folder, query string, hash)
    let newRoute = this.$_RouteMixin_getRouteCopy()
    newRoute['name'] = newRoute['name'].replace(this.reportYear, this.selectedYear)
    let dbn = newRoute.params.dbn
    if (dbn) {
      const newDbn = this.dbnChange(dbn, this.reportYear, this.selectedYear)
      if (newDbn !== dbn) {
        newRoute.params = Object.assign({}, newRoute.params, {dbn: newDbn})
        // this works for other report besides 'sqs', if they implement 'setRouteSchool'

        // if we don't map muttation, we can do the following instead
        // this.$store.commit(`${this.reportCode}/setRouteSchool`, newRoute.params)
        this.setRouteSchool(newRoute.params)
      }
    }

    this.$router.push(newRoute)
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
</style>



// WEBPACK FOOTER //
// src/components/InputSelectYear.vue